import {
    Directive,
    ElementRef,
    Input,
    OnInit,
    Renderer2,
    OnDestroy,
    Output,
    EventEmitter,
    AfterViewInit
} from '@angular/core';

@Directive({
    selector: '[materialize="material_chip"], appMaterializeLabelFix'
})
export class MaterialChipDirective implements OnInit, AfterViewInit, OnDestroy {

    @Input('materializeParams') params: any[] = [];

    /** Maximum number of characters allowed per chip; also drives the character counter. */
    @Input() maxChars: number | string;

    @Output() chipsChange = new EventEmitter<string[]>();

    private inputEl: HTMLInputElement;
    private chips: string[] = [];
    private listeners: Array<() => void> = [];
    private label: HTMLElement | null = null; // addedNew
    private counterEl: HTMLElement | null = null;

    constructor(private el: ElementRef, private renderer: Renderer2) { }

    ngOnInit() {
        const host = this.el.nativeElement;
        this.renderer.addClass(host, 'chips');

        this.inputEl = this.renderer.createElement('input');
        this.renderer.addClass(this.inputEl, 'input');
        this.renderer.appendChild(host, this.inputEl);

        const max = Number(this.maxChars);
        if (max > 0) {
            this.renderer.setAttribute(this.inputEl, 'maxlength', `${max}`);
            this.createCounter(max);

            this.listeners.push(
                this.renderer.listen(this.inputEl, 'input', () => this.updateCounter(max))
            );
        }

        const initial = this.params?.[0]?.data || [];
        initial.forEach((chip: any) => this.addChip(chip.tag || chip));

        // Listen for Enter key
        this.listeners.push(
            this.renderer.listen(this.inputEl, 'keydown', (e: KeyboardEvent) => {
                if (e.key === 'Enter' && this.inputEl.value.trim()) {
                    this.addChip(this.inputEl.value.trim());
                    this.inputEl.value = '';
                    e.preventDefault();
                    this.updateLabelState();
                    this.updateCounter(Number(this.maxChars));
                }
            })
        );

        // Label events on the input
        this.listeners.push(
            this.renderer.listen(this.inputEl, 'focus', () => {
                this.updateLabelState();
            })
        );

        this.listeners.push(
            this.renderer.listen(this.inputEl, 'blur', () => {
                this.updateLabelState();
            })
        );
    }

    ngAfterViewInit() {
        // Find label after view is ready
        this.findLabel();
        // Set initial label state
        this.updateLabelState();
    }

    private createCounter(max: number) {
        this.counterEl = this.renderer.createElement('span');
        this.renderer.addClass(this.counterEl, 'chips-character-counter');
        this.renderer.appendChild(this.el.nativeElement.parentNode, this.counterEl);
        this.updateCounter(max);
    }

    private updateCounter(max: number) {
        if (!this.counterEl || !(max > 0)) {
            return;
        }

        this.counterEl.textContent = `${this.inputEl.value.length} / ${max}`;
    }

    private findLabel() {
        const host = this.el.nativeElement as HTMLElement;
        const hostId = host.getAttribute('id');

        if (hostId) {
            const lbl = document.querySelector(`label[for="${hostId}"]`);
            if (lbl) {
                this.label = lbl as HTMLElement;
                return;
            }
        }

        const nextSibling = host.nextElementSibling;
        if (nextSibling?.tagName === 'LABEL') {
            this.label = nextSibling as HTMLElement;
            return;
        }

        let parent = host.parentElement;
        while (parent) {
            if (parent.classList.contains('input-field')) {
                const lbl = parent.querySelector('label');
                if (lbl) {
                    this.label = lbl as HTMLElement;
                    return;
                }
            }
            parent = parent.parentElement;
        }
    }

    private updateLabelState() {
        if (!this.label) {
            this.findLabel();
            if (!this.label) return;
        }

        const hasChips = this.chips.length > 0;
        const isFocused = document.activeElement === this.inputEl;
        const hasValue = this.inputEl.value.trim().length > 0;

        if (hasChips || isFocused || hasValue) {
            this.renderer.addClass(this.label, 'active');
        } else {
            this.renderer.removeClass(this.label, 'active');
        }
    }

    private addChip(text: string) {
        this.chips.push(text);

        const chipEl = this.renderer.createElement('div');
        this.renderer.addClass(chipEl, 'chip');

        chipEl.textContent = text;

        const closeIcon = this.renderer.createElement('i');
        this.renderer.addClass(closeIcon, 'material-icons');
        this.renderer.addClass(closeIcon, 'close');
        closeIcon.textContent = 'close';
        this.renderer.setStyle(closeIcon, 'cursor', 'pointer');
        this.renderer.appendChild(chipEl, closeIcon);

        this.renderer.insertBefore(
            this.el.nativeElement,
            chipEl,
            this.inputEl
        );

        // Remove chip on click
        this.listeners.push(
            this.renderer.listen(closeIcon, 'click', () => {
                this.removeChip(text, chipEl);
            })
        );

        this.chipsChange.emit([...this.chips]);
        this.updateLabelState();
    }

    private removeChip(text: string, chipEl: HTMLElement) {
        this.chips = this.chips.filter(c => c !== text);
        this.renderer.removeChild(this.el.nativeElement, chipEl);

        this.chipsChange.emit([...this.chips]);
        this.updateLabelState();
    }

    ngOnDestroy() {
        this.listeners.forEach(unlisten => unlisten());

        if (this.counterEl?.parentNode) {
            this.renderer.removeChild(this.counterEl.parentNode, this.counterEl);
        }
    }
}
